/**
 * The signed-in person, in the shell's top bar: a person glyph that opens the shell's one identity
 * surface — the Account ID being acted on, the person's name, their role in that Account, and the two
 * identity controls (through to the profile page, and out of the session).
 *
 * ## Why a popover rather than the header text it replaces
 *
 * The header used to print the Account and the role as standing text — permanent chrome for information
 * a reader wants perhaps twice a session. It now collapses to one glyph that expands on demand, and it is
 * the *only* place the shell says who you are: the rail carries no Account label and no role chip, so
 * there is one surface to look at rather than three saying overlapping things.
 *
 * ## Where each value comes from, which is not the same place
 *
 *  - **`name` comes from the token** ({@link identityFromUser}). It is a property of the person and does
 *    not change with the selected Account, so the Account-independent source is the right one.
 *  - **The Account ID and the role come from {@link useSelectedAccount}**, never from the token. The
 *    token carries the *home* Account and the role in it; the selected Account is what every page below
 *    is acting on, and the same person can be `admin` at home and `viewer` in a shared Account. A panel
 *    naming the home Account while the pages read and write a shared one would be a lie — and showing
 *    the home role would contradict the controls those pages render, which is the exact bug the Account
 *    switcher was built to remove.
 *
 * The opaque Cognito `sub` used to be this panel's first row. Nothing is lost by dropping it: the
 * profile page renders it as its "User id" row, one click away through "Manage Profile", and it is an
 * identifier a reader needs when matching their own row in the members table — not when checking which
 * workspace they are in.
 *
 * ## Accessibility
 *
 * Modelled on {@link import('./InfoTooltip').InfoTooltip}, the house pattern for a revealed panel: the
 * trigger owns `aria-expanded`, `aria-controls` is applied **only while open** so the reference never
 * dangles at a node that does not exist, `Escape` closes it from anywhere inside *and hands focus back to
 * the trigger*, and a pointer press outside closes it. The glyph is `aria-hidden` with the trigger named
 * by `aria-label`, because a picture of a person is not a label.
 *
 * Both handlers sit on the wrapper rather than on any one child, which is why the panel can hold two
 * focusable controls without either escape route weakening: `Escape` closes and restores focus from the
 * link and from the button alike, and a press on either is a press inside the panel rather than a
 * dismissal.
 *
 * No `aria-haspopup`. Its default value is `menu`, which would announce a menu and invite arrow-key
 * navigation of a description list, a link, and a button — `aria-expanded` already says the trigger
 * reveals something, and it is the only claim here that is true.
 *
 * ## Sign-out lives in here, behind the disclosure
 *
 * It used to stand in the top bar, on the argument that ending a session is the one thing a person may
 * need in a hurry and a disclosure in front of it is an interaction too many. That was asked directly and
 * decided the other way: identity is one surface, and sign-out is an identity control, so it sits with
 * the rest of them rather than beside the glyph that hides them. The cost is one click, paid by the
 * rarest interaction in the shell; what it buys is a top bar with nothing standing in it and no second
 * place to look for "who am I and how do I leave".
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';

import { useSelectedAccount } from '../account-context';
import { identityFromUser } from '../auth/identity';

/**
 * A filled person: a circle head above rounded shoulders.
 *
 * Inline rather than an `<img src="…svg">` so it inherits `currentColor` — the glyph is muted until the
 * panel is open or the pointer is over it, and a referenced image cannot follow a colour it does not know
 * about. It also costs no second network request, for two shapes.
 *
 * `focusable="false"` alongside `aria-hidden`: without it, IE-era Edge and some assistive tooling put SVG
 * elements in the tab order, which would give the trigger two tab stops.
 */
function UserGlyph(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[1.375rem] fill-current"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="7.75" r="4.25" />
      <path d="M12 13.5c-4.28 0-7.75 2.63-7.75 5.88 0 .9.73 1.62 1.63 1.62h12.24c.9 0 1.63-.73 1.63-1.62 0-3.25-3.47-5.88-7.75-5.88Z" />
    </svg>
  );
}

/**
 * An absent display name, stated (the rule the profile page already follows). A blank line beside the
 * label "Name" reads as a rendering fault; "Not set" reads as an empty field, which is what it is.
 */
function orNotSet(value: string | undefined): string {
  return value !== undefined && value.length > 0 ? value : 'Not set';
}

export function UserPanel(): JSX.Element {
  const auth = useAuth();
  const { accountId, role } = useSelectedAccount();
  const { name } = identityFromUser(auth.user);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      // A press inside the panel is a press on the panel's own controls, so only an outside one closes.
      // Deliberately no focus restoration here: the pointer has already chosen where attention goes, and
      // pulling focus back to the trigger would yank it off whatever was just clicked.
      if (containerRef.current?.contains(event.target as Node) === true) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    // `onKeyDown` on the wrapper rather than on the trigger so Escape still closes the panel once focus
    // has moved into it — otherwise a keyboard user who tabbed to "Manage Profile", or past it to "Sign
    // out", has no way back out. One handler covers every focusable child, however many there are.
    <div
      className="relative"
      ref={containerRef}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return;
        event.stopPropagation();
        setOpen(false);
        // Escape is a keyboard gesture, so the keyboard has to land somewhere. Unmounting the panel while
        // focus is inside it would drop focus to `<body>` and lose the reader's place in the page
        // entirely — the tab order restarts from the top of the document.
        triggerRef.current?.focus();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="rounded-full border-transparent bg-transparent p-1.5 text-muted transition-colors hover:bg-white/8 hover:text-ink aria-expanded:bg-white/8 aria-expanded:text-ink"
        aria-label="Your account"
        aria-expanded={open}
        // Only while the panel exists. A permanent `aria-controls` pointing at an unmounted id is a
        // reference to nothing, which is worse than no reference at all.
        {...(open ? { 'aria-controls': panelId } : {})}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <UserGlyph />
      </button>

      {open ? (
        <div
          id={panelId}
          className="absolute right-0 top-full z-30 mt-2 w-72 rounded-card border border-hairline bg-elevated p-4 shadow-[0_12px_32px_rgb(0_0_0/45%)]"
        >
          {/* No `aria-label` on the list: `<dl>` has no consistently mapped role, so a name on it is not
              reliably announced. Each `<dt>` labels its own `<dd>`, which is announced everywhere. */}
          <dl className="grid gap-3">
            <div className="grid gap-0.5">
              <dt className="text-[0.6875rem] font-medium uppercase tracking-[0.05em] text-muted">
                Account ID
              </dt>
              {/* The **selected** Account, not the token's home one — see the module doc. Monospaced and
                  allowed to wrap: it is a generated identifier a reader quotes into a support thread, and
                  truncating something meant to be copied defeats the reason it is shown.

                  "Unknown" rather than "Not set", the same rule the role row follows: an Account ID is
                  always present once the Accounts list has arrived, so an absent one is a load that has
                  not finished or has failed, not an empty field. */}
              <dd className="break-all font-mono text-[0.75rem] text-ink" data-testid="user-panel-id">
                {accountId ?? 'Unknown'}
              </dd>
            </div>
            <div className="grid gap-0.5">
              <dt className="text-[0.6875rem] font-medium uppercase tracking-[0.05em] text-muted">
                Name
              </dt>
              <dd className="text-ink" data-testid="user-panel-name">
                {orNotSet(name)}
              </dd>
            </div>
            <div className="grid gap-0.5">
              <dt className="text-[0.6875rem] font-medium uppercase tracking-[0.05em] text-muted">
                Role
              </dt>
              {/* "Unknown" rather than "Not set": a role is always set once the Account list has arrived,
                  so an absent one is a load that has not finished or has failed — not an empty field. */}
              <dd className="text-ink" data-testid="user-panel-role">
                {role ?? 'Unknown'}
              </dd>
            </div>
          </dl>
          {/* The two identity controls, as a stacked pair sharing the panel's width. A grid rather than
              two margins so they cannot drift apart, and so both stretch to the same edge — a pair of
              controls at different widths reads as two unrelated things that happen to be adjacent. */}
          <div className="mt-4 grid gap-1.5">
            <Link
              to="/profile"
              className="flex justify-center rounded-control border border-hairline bg-surface px-3 py-1.5 text-[0.8125rem] font-medium text-ink no-underline hover:border-muted"
              onClick={() => setOpen(false)}
            >
              Manage Profile
            </Link>
            {/*
              The subordinate of the two: same shape, same hairline, but no fill and a muted label, so the
              eye lands on "Manage Profile" first. It still reads as a control rather than as a link,
              which a borderless label would not.

              Every utility here is neutralising the base `button` rule, which paints the accent fill
              because the pages write `<button>` for the affirmative action. The utilities layer outranks
              base, hover included.

              `removeUser` clears the local OIDC session only, exactly as the top-bar control it replaced
              did; the auth guard then puts the Sign in screen back. Federated logout through Cognito's
              Hosted-UI `/logout` endpoint is a separate documented follow-up. No `setOpen(false)`: a
              successful sign-out unmounts this whole shell, and closing the panel first would only make
              a *failed* one look like a click that did nothing.
            */}
            <button
              type="button"
              className="rounded-control border border-hairline bg-transparent px-3 py-1.5 text-[0.8125rem] font-medium text-muted hover:border-muted hover:bg-surface hover:text-ink"
              onClick={() => void auth.removeUser()}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
